import {
  Boxes,
  FileSignature,
  Home,
  LayoutDashboard,
  LayoutGrid,
  Package,
  Workflow,
} from 'lucide-react';
import { WAREHOUSE_ROOT, isWarehousePath } from '../../layouts/areaSwitch';

/**
 * The seller's sidebar, from the legacy `CustomLayout`. The shape is documented
 * in `Sidebar`.
 */
export const sellerNavigation = [
  { type: 'link', to: '/home', icon: Home, label: 'Inicio' },
  { type: 'link', to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  {
    type: 'section',
    key: 'deliveries',
    to: '/deliveries',
    icon: Package,
    label: 'Deliveries',
    children: [
      { to: '/deliveries', label: 'Historial', end: true },
      { to: '/deliveries/attention', label: 'Alerta' },
    ],
  },
  {
    type: 'group',
    key: 'panel',
    icon: LayoutGrid,
    label: 'Panel de Control',
    children: [
      { to: '/configuration/ecommerce', label: 'Integración E-commerce' },
      { to: '/configuration/webhooks', label: 'Webhooks' },
      { to: '/pickup-requests/create', label: 'Solicitudes de retiro' },
      { to: '/pickup-requests/assign', label: 'Asignar a manifiesto' },
    ],
  },
];

export { WAREHOUSE_ROOT };

const myContracts = {
  type: 'link',
  to: `${WAREHOUSE_ROOT}/clients`,
  icon: FileSignature,
  label: 'Mis contratos',
};

/**
 * The sidebar once inside the module, for a seller that keeps its goods in
 * somebody else's centre: it follows its orders, declares what is coming, and
 * looks at its stock, its catalogue and the shelves. Putting away and the
 * packing supplies are the operator's work, so their entries are not drawn.
 * The way back to the OMS is not in it either: the sidebar offers it under
 * the user, on both sides (`layouts/areaSwitch`).
 */
export const sellerWarehouseNavigation = [
  {
    type: 'group',
    key: 'warehouse-workflows',
    icon: Workflow,
    label: 'Workflows',
    children: [
      { to: `${WAREHOUSE_ROOT}/orders`, label: 'Órdenes' },
      { to: `${WAREHOUSE_ROOT}/receipts`, label: 'Recepción' },
    ],
  },
  {
    type: 'group',
    key: 'warehouse-inventory',
    icon: Boxes,
    label: 'Inventario y ubicaciones',
    children: [
      { to: `${WAREHOUSE_ROOT}/inventory`, label: 'Inventario' },
      { to: `${WAREHOUSE_ROOT}/movements`, label: 'Movimientos' },
      { to: `${WAREHOUSE_ROOT}/products`, label: 'Productos' },
      { to: `${WAREHOUSE_ROOT}/locations`, label: 'Ubicaciones' },
    ],
  },
  myContracts,
];

/**
 * The same, for a seller that holds a warehouse of its own: it also puts away
 * and keeps its packing supplies there. Holding one is enough to draw the
 * entries; inside, each screen still asks the server which warehouse allows it.
 */
export const sellerWarehouseHolderNavigation = [
  {
    type: 'group',
    key: 'warehouse-workflows',
    icon: Workflow,
    label: 'Workflows',
    children: [
      { to: `${WAREHOUSE_ROOT}/orders`, label: 'Órdenes' },
      { to: `${WAREHOUSE_ROOT}/receipts`, label: 'Recepción' },
      { to: `${WAREHOUSE_ROOT}/putaway`, label: 'Almacenado' },
    ],
  },
  {
    type: 'group',
    key: 'warehouse-inventory',
    icon: Boxes,
    label: 'Inventario y ubicaciones',
    children: [
      { to: `${WAREHOUSE_ROOT}/inventory`, label: 'Inventario' },
      { to: `${WAREHOUSE_ROOT}/movements`, label: 'Movimientos' },
      { to: `${WAREHOUSE_ROOT}/products`, label: 'Productos' },
      { to: `${WAREHOUSE_ROOT}/supplies`, label: 'Insumos' },
      { to: `${WAREHOUSE_ROOT}/locations`, label: 'Ubicaciones' },
    ],
  },
  myContracts,
];

/**
 * Which menu the seller's sidebar shows. Same rule as the admin's: the module
 * is a place, and being under `/warehouse` is what turns the sidebar into the
 * module's. What differs is that the module's menu depends on whether the
 * seller holds a warehouse of its own.
 *
 * Every answer is a module-level constant, which `Sidebar` requires.
 *
 * @param {object} params
 * @param {boolean} params.hasWarehouse - Whether the plan carries the module.
 * @param {boolean} params.holdsWarehouse - Whether any warehouse in reach is the seller's own.
 * @param {string} params.pathname
 * @returns {{navigation: Array<object>, inWarehouse: boolean}}
 */
export const pickSellerNavigation = ({ hasWarehouse, holdsWarehouse, pathname }) => {
  if (!hasWarehouse) return { navigation: sellerNavigation, inWarehouse: false };

  const inWarehouse = isWarehousePath(pathname);
  if (!inWarehouse) return { navigation: sellerNavigation, inWarehouse };

  return {
    navigation: holdsWarehouse ? sellerWarehouseHolderNavigation : sellerWarehouseNavigation,
    inWarehouse,
  };
};
